/**
 * Public restaurant claim/verified status (get_restaurant_public_status RPC).
 * SECURITY DEFINER — safe for anon/authenticated; no PII.
 * Use for marketing claim CTAs, map badges, and web share pages — not for authorization.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

export type RestaurantPublicStatus = {
  is_claimed: boolean;
  is_verified: boolean;
  has_pro_marker?: boolean;
  marker_variant?: 'standard' | 'pro';
  restaurant_plan?: string;
};

/** Picker card icons: MH = claimed/verified owner; Google = linked Google Place. */
export type RestaurantPickerSourceBadge = "menuhunter" | "google";

/**
 * Build picker source badges.
 * - menuhunter: restaurant is publicly claimed or verified (not merely in our DB)
 * - google: has a Google Place id
 */
export function buildRestaurantPickerBadges(input: {
  isClaimed?: boolean | null;
  isVerified?: boolean | null;
  hasGooglePlaceId?: boolean | null;
}): RestaurantPickerSourceBadge[] {
  const badges: RestaurantPickerSourceBadge[] = [];
  if (input.isClaimed === true || input.isVerified === true) {
    badges.push("menuhunter");
  }
  if (input.hasGooglePlaceId) {
    badges.push("google");
  }
  return badges;
}

/** Resolve merge chain to canonical restaurant id (RLS-safe via SECURITY DEFINER RPC). */
export async function resolveCanonicalRestaurantId(
  supabaseClient: SupabaseClient,
  restaurantId: string
): Promise<string> {
  if (!restaurantId) return restaurantId;
  const { data, error } = await supabaseClient.rpc("canonical_restaurant_id", {
    p_restaurant_id: restaurantId,
  });
  if (error || data == null || data === "") return restaurantId;
  return String(data);
}

/**
 * Product rule for “publicly claimed” (claim CTA / banners): active/approved claim OR verified manager.
 * Undefined when pub is missing (unknown — do not treat as unclaimed).
 */
export function isPubliclyClaimed(pub: RestaurantPublicStatus | undefined): boolean | undefined {
  return pub ? !!(pub.is_claimed || pub.is_verified) : undefined;
}

/**
 * Fetches public claim/verified status for the given restaurant IDs (canonical or raw — one row per id).
 * Returns a map keyed by the id returned from the RPC and an optional RPC error.
 */
export async function fetchRestaurantPublicStatus(
  supabaseClient: SupabaseClient,
  restaurantIds: string[]
): Promise<{
  map: Map<string, RestaurantPublicStatus>;
  error: Error | null;
}> {
  const unique = [...new Set(restaurantIds)].filter(Boolean);
  if (unique.length === 0) return { map: new Map(), error: null };

  const { data, error } = await supabaseClient.rpc("get_restaurant_public_status", {
    restaurant_ids: unique,
  });

  if (error) {
    console.error("[publicRestaurantStatus] Error fetching public status:", error);
    return { map: new Map(), error: error as Error };
  }

  const rows = (data ?? []) as Array<{
    restaurant_id: string;
    is_claimed: boolean;
    is_verified: boolean;
    has_pro_marker?: boolean;
    marker_variant?: string;
    restaurant_plan?: string;
  }>;
  const map = new Map<string, RestaurantPublicStatus>();
  rows.forEach((row) => {
    const markerVariant =
      row.marker_variant === 'pro' ? 'pro' : ('standard' as const);
    map.set(row.restaurant_id, {
      is_claimed: !!row.is_claimed,
      is_verified: !!row.is_verified,
      has_pro_marker: row.has_pro_marker === true,
      marker_variant: markerVariant,
      restaurant_plan: row.restaurant_plan ?? undefined,
    });
  });
  return { map, error: null };
}

/**
 * Attach claim-aware picker badges for suggestion rows that may have restaurant_id.
 * Google badge uses place_id; MH badge only when publicly claimed/verified.
 */
export async function attachRestaurantPickerClaimBadges<
  T extends {
    restaurant_id?: string | null;
    place_id?: string | null;
    badges?: RestaurantPickerSourceBadge[];
  },
>(supabaseClient: SupabaseClient, items: T[]): Promise<T[]> {
  const ids = [
    ...new Set(
      items
        .map((item) => item.restaurant_id)
        .filter((id): id is string => typeof id === "string" && id.length > 0),
    ),
  ];
  const { map } =
    ids.length > 0
      ? await fetchRestaurantPublicStatus(supabaseClient, ids)
      : { map: new Map<string, RestaurantPublicStatus>() };

  return items.map((item) => {
    const pub = item.restaurant_id ? map.get(item.restaurant_id) : undefined;
    return {
      ...item,
      badges: buildRestaurantPickerBadges({
        isClaimed: pub?.is_claimed,
        isVerified: pub?.is_verified,
        hasGooglePlaceId: !!item.place_id,
      }),
    };
  });
}

/**
 * For each snapshot `restaurant_id` (possibly non-canonical), resolve canonical id, then batch-fetch
 * public status for unique canonicals. Returns status keyed by the **original** snapshot id so
 * callers can join with `menu_snapshots.restaurant_id` without re-mapping.
 */
export async function fetchPublicStatusBySnapshotRestaurantIds(
  supabaseClient: SupabaseClient,
  snapshotRestaurantIds: string[]
): Promise<{
  byRawId: Map<string, RestaurantPublicStatus | undefined>;
  error: Error | null;
}> {
  const uniqueRaw = [...new Set(snapshotRestaurantIds)].filter(Boolean);
  if (uniqueRaw.length === 0) return { byRawId: new Map(), error: null };

  const pairs = await Promise.all(
    uniqueRaw.map(async (raw) => {
      const canonical = await resolveCanonicalRestaurantId(supabaseClient, raw);
      return { raw, canonical };
    })
  );

  const uniqueCanonicals = [...new Set(pairs.map((p) => p.canonical))];
  const { map: publicMap, error } = await fetchRestaurantPublicStatus(supabaseClient, uniqueCanonicals);

  const byRawId = new Map<string, RestaurantPublicStatus | undefined>();
  if (error) {
    uniqueRaw.forEach((id) => byRawId.set(id, undefined));
    return { byRawId, error };
  }

  for (const { raw, canonical } of pairs) {
    byRawId.set(raw, publicMap.get(canonical));
  }
  return { byRawId, error: null };
}
